/**
 * 콜봇 월 정산 화면의 숫자·시간·KST 월 표기 (순수 함수).
 *
 * 요청: ~/md/front/admin-콜봇-월정산-프론트요청.md "화면 명세 4. 공통", "요청 계약 0"
 *  - 원은 천 단위 콤마. 확정 금액은 정수 그대로, 중간값(686.4, 50,894.91)은 소수 둘째 자리까지.
 *  - 단가는 소수 그대로(33.33, 42.9). 시간은 `n시간 n분 n초`. 시각은 KST.
 *  - 회선·상담사 번호는 끝 4자리만(`···4453`). 고객 번호는 어디에도 그리지 않는다.
 *  - 값이 없거나 숫자가 아니면(서버의 미집계 `"…"` 포함) `—` 로 보인다.
 *
 * KST 는 서머타임이 없는 UTC+9 고정이라 dayjs·Intl 없이 9시간을 더한 UTC 필드로 읽는다.
 * 월은 `YYYY-MM` 문자열로만 다룬다(KST 월 [월초, 익월초)).
 * '@/'·vue·antd 를 import 하지 않는다 — node --test 로 검증한다
 * (service/__tests__/settlementFormat.test.mjs).
 */

const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/** 값이 없을 때 보이는 표기 */
export const EMPTY = '—';

/** 끝 4자리 앞에 붙이는 가림 표기(가운뎃점 셋) */
export const MASK_PREFIX = '···';

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * 유한한 숫자인가 — 미집계 `"…"`·null·NaN 은 아니다.
 * @param {unknown} n
 * @returns {n is number}
 */
export function isNumber(n) {
  return typeof n === 'number' && Number.isFinite(n);
}

// ---------------------------------------------------------------------------
// 숫자
// ---------------------------------------------------------------------------

/**
 * 천 단위 콤마. 소수는 `maxDecimals` 자리에서 반올림하고 끝의 0 을 지운다. 정수는 바꾸지 않는다.
 * @param {unknown} n
 * @param {{ maxDecimals?: number }} [opts]
 * @returns {string} 예: 1234 → '1,234', 50894.91 → '50,894.91', 493.888 → '493.89', '…' → '—'
 */
export function formatNumber(n, { maxDecimals = 2 } = {}) {
  if (!isNumber(n)) return EMPTY;
  const abs = Math.abs(n);
  let text = Number.isInteger(abs) ? String(abs) : abs.toFixed(maxDecimals);
  if (text.includes('.')) text = text.replace(/0+$/, '').replace(/\.$/, '');
  const [int, frac] = text.split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const body = frac ? `${grouped}.${frac}` : grouped;
  // -0.001 처럼 반올림 뒤 0 이 되면 부호를 붙이지 않는다
  return n < 0 && body !== '0' ? `-${body}` : body;
}

/**
 * 원 표기. 확정 금액(정수)은 그대로, 중간값은 소수 둘째 자리까지.
 * @param {unknown} n
 * @returns {string} 예: '58,894원', '686.4원', '50,894.91원', null → '—'
 */
export function formatWon(n) {
  return isNumber(n) ? `${formatNumber(n)}원` : EMPTY;
}

/**
 * 단가 표기(소수 그대로). 단위는 붙이지 않는다 — formatRateValue 가 붙인다.
 * @param {unknown} n
 * @returns {string} 예: '33.33', '42.9', '1,000'
 */
export function formatUnitPrice(n) {
  return formatNumber(n, { maxDecimals: 4 });
}

/**
 * 개수 + 단위. 값이 없으면 단위 없이 '—'.
 * @param {unknown} n
 * @param {string} [unit] '통'·'건'·'곳'·'개' 등
 * @returns {string} 예: formatCount(1775, '통') → '1,775통'
 */
export function formatCount(n, unit = '') {
  return isNumber(n) ? `${formatNumber(n)}${unit}` : EMPTY;
}

/**
 * 금액 입력 칸(a-input-number `formatter`)의 천 단위 콤마 — 보이는 글자만 바꾼다. 소수 부분은 건드리지 않는다.
 * 검토 폼 줄 금액·일회성 항목 금액이 같이 쓴다.
 * @param {unknown} value 입력 칸의 값(숫자 또는 입력 중인 글자)
 * @returns {string} 예: 127000 → '127,000', '1234.5' → '1,234.5', null·'' → ''
 */
export function formatThousandsInput(value) {
  if (value === null || value === undefined || value === '') return '';
  const [int, frac] = String(value).split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return frac === undefined ? grouped : `${grouped}.${frac}`;
}

/**
 * formatThousandsInput 의 반대(a-input-number `parser`) — 콤마와 앞뒤 공백을 지운다.
 * @param {unknown} text
 * @returns {string} 예: '127,000' → '127000', null → ''
 */
export function parseThousandsInput(text) {
  return String(text ?? '').replace(/,/g, '').trim();
}

/**
 * 초 → `n시간 n분 n초`. 앞쪽의 0 단위만 뺀다(시간이 있으면 0분·0초도 보인다).
 * @param {unknown} sec
 * @returns {string} 예: 13971 → '3시간 52분 51초', 889 → '14분 49초', 0 → '0초', 7205 → '2시간 0분 5초'
 */
export function formatDuration(sec) {
  if (!isNumber(sec)) return EMPTY;
  const total = Math.max(0, Math.round(sec));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${formatNumber(h)}시간 ${m}분 ${s}초`;
  if (m > 0) return `${m}분 ${s}초`;
  return `${s}초`;
}

/**
 * 회선·상담사 번호를 끝 4자리만 보인다. 전체 번호를 받아도 숫자만 남겨 끝 4자리를 쓴다.
 * 고객 번호(`caller_number`)는 이 함수로도 그리지 않는다 — 화면에 넘기지 않는다.
 * @param {unknown} numOrLast4
 * @returns {string} 예: '07000004453' → '···4453', '4453' → '···4453', null → '—'
 */
export function maskNumber(numOrLast4) {
  if (numOrLast4 === null || numOrLast4 === undefined) return EMPTY;
  const digits = String(numOrLast4).replace(/\D/g, '');
  return digits ? `${MASK_PREFIX}${digits.slice(-4)}` : EMPTY;
}

// ---------------------------------------------------------------------------
// KST 시각
// ---------------------------------------------------------------------------

/**
 * unix ms → KST 달력 값
 * @param {number} ms
 * @returns {{ year: number, month: number, day: number, hour: number, minute: number }}
 */
export function kstParts(ms) {
  const d = new Date(ms + KST_OFFSET_MS);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
  };
}

/**
 * @param {unknown} ms unix ms
 * @returns {string} 'YYYY-MM-DD HH:mm'(KST)
 */
export function formatKstDateTime(ms) {
  if (!isNumber(ms)) return EMPTY;
  return `${formatKstDate(ms)} ${formatKstTime(ms)}`;
}

/**
 * @param {unknown} ms unix ms
 * @returns {string} 'YYYY-MM-DD'(KST)
 */
export function formatKstDate(ms) {
  if (!isNumber(ms)) return EMPTY;
  const p = kstParts(ms);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
}

/**
 * @param {unknown} ms unix ms
 * @returns {string} 'HH:mm'(KST)
 */
export function formatKstTime(ms) {
  if (!isNumber(ms)) return EMPTY;
  const p = kstParts(ms);
  return `${pad2(p.hour)}:${pad2(p.minute)}`;
}

/**
 * `nowMs` 뒤에 오는 첫 KST `hour:00` 시각(지금이 정각이면 다음 날).
 * 자동 마감 잡(매일 06:00 KST)의 다음 실행 시각을 셀 때 쓴다.
 * @param {number} nowMs
 * @param {number} [hour]
 * @returns {number} unix ms
 */
export function nextKstHourMs(nowMs, hour = 6) {
  const p = kstParts(nowMs);
  const todayAt = Date.UTC(p.year, p.month - 1, p.day, hour) - KST_OFFSET_MS;
  return todayAt > nowMs ? todayAt : todayAt + DAY_MS;
}

/**
 * KST 날짜 기준 '오늘' / '내일' / 'YYYY-MM-DD'
 * @param {number} targetMs
 * @param {number} [nowMs]
 * @returns {string}
 */
export function relativeKstDayLabel(targetMs, nowMs = Date.now()) {
  const target = formatKstDate(targetMs);
  if (target === formatKstDate(nowMs)) return '오늘';
  if (target === formatKstDate(nowMs + DAY_MS)) return '내일';
  return target;
}

// ---------------------------------------------------------------------------
// KST 월 (`YYYY-MM`)
// ---------------------------------------------------------------------------

/**
 * @param {unknown} s
 * @returns {boolean} 'YYYY-MM'(01~12) 인가
 */
export function isValidMonth(s) {
  return typeof s === 'string' && MONTH_RE.test(s);
}

/** @param {string} month @returns {[number, number]} [연, 월(1~12)] */
function splitMonth(month) {
  const [y, m] = month.split('-');
  return [Number(y), Number(m)];
}

/**
 * @param {number} [nowMs]
 * @returns {string} 지금의 KST 월 'YYYY-MM'
 */
export function currentMonthKst(nowMs = Date.now()) {
  const p = kstParts(nowMs);
  return `${p.year}-${pad2(p.month)}`;
}

/**
 * 지난 달(KST) — 목록·기본 요율 화면의 기본 월
 * @param {number} [nowMs]
 * @returns {string}
 */
export function previousMonthKst(nowMs = Date.now()) {
  return addMonths(currentMonthKst(nowMs), -1);
}

/**
 * 다음 달(KST) — 기본 요율 `[새 버전]` 의 기본 적용 시작 월
 * @param {number} [nowMs]
 * @returns {string}
 */
export function nextMonthKst(nowMs = Date.now()) {
  return addMonths(currentMonthKst(nowMs), 1);
}

/**
 * @param {string} month 'YYYY-MM'
 * @param {number} n 더할 달 수(음수 가능)
 * @returns {string|null} 형식이 틀리면 null
 */
export function addMonths(month, n) {
  if (!isValidMonth(month)) return null;
  const [y, m] = splitMonth(month);
  const index = y * 12 + (m - 1) + n;
  const year = Math.floor(index / 12);
  return `${String(year).padStart(4, '0')}-${pad2(index - year * 12 + 1)}`;
}

/**
 * 'YYYY-MM' 끼리 비교(자릿수가 고정이라 문자열 비교와 같다)
 * @param {string} a
 * @param {string} b
 * @returns {-1|0|1}
 */
export function compareMonth(a, b) {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/**
 * @param {string} month
 * @returns {string} '2026-09' → '2026년 9월'
 */
export function monthLabel(month) {
  if (!isValidMonth(month)) return EMPTY;
  const [y, m] = splitMonth(month);
  return `${y}년 ${m}월`;
}

/**
 * @param {string} month
 * @param {number} [nowMs]
 * @returns {boolean} 진행 중인 달(KST)인가
 */
export function isCurrentMonth(month, nowMs = Date.now()) {
  return month === currentMonthKst(nowMs);
}

/**
 * 끝난 달인가(이번 KST 월보다 앞). 수동 마감 실행(close-runs)은 끝난 달만 받는다.
 * @param {string} month
 * @param {number} [nowMs]
 * @returns {boolean}
 */
export function isMonthEnded(month, nowMs = Date.now()) {
  return compareMonth(month, currentMonthKst(nowMs)) < 0;
}

/**
 * @param {string} month
 * @returns {number} 그 달의 일수(형식이 틀리면 0)
 */
export function daysInMonth(month) {
  if (!isValidMonth(month)) return 0;
  const [y, m] = splitMonth(month);
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/**
 * KST 월초(1일 00:00 KST)
 * @param {string} month
 * @returns {number} unix ms (형식이 틀리면 NaN)
 */
export function monthStartMsKst(month) {
  if (!isValidMonth(month)) return NaN;
  const [y, m] = splitMonth(month);
  return Date.UTC(y, m - 1, 1) - KST_OFFSET_MS;
}

/**
 * KST 익월초 — 그 달 구간 [monthStartMsKst, monthEndMsKst) 의 끝(미포함)
 * @param {string} month
 * @returns {number} unix ms
 */
export function monthEndMsKst(month) {
  return monthStartMsKst(addMonths(month, 1));
}

/**
 * 그 달을 마감하는 첫 자동 실행 시각 = 다음 달 `closeDay` 일 `hour`:00 KST
 * (요청 계약 4-1 `BILLING_AUTO_CLOSE_DAY` 기본 3, `BILLING_AUTO_CLOSE_HOUR` 기본 6).
 * @param {string} month
 * @param {{ closeDay?: number, hour?: number }} [opts]
 * @returns {number} unix ms (형식이 틀리면 NaN)
 */
export function autoCloseAtMs(month, { closeDay = 3, hour = 6 } = {}) {
  if (!isValidMonth(month)) return NaN;
  const [y, m] = splitMonth(month);
  // Date.UTC 의 월 인덱스는 0 부터라 1~12 월 값 m 을 그대로 넣으면 다음 달이다(12 → 다음 해 1월)
  return Date.UTC(y, m, closeDay, hour) - KST_OFFSET_MS;
}

/**
 * 라우트 쿼리 `month` → 쓸 달. 형식이 맞으면 그대로, 아니면(없음 포함) 지난 달(KST).
 * vue-router 가 같은 키를 여러 번 받으면 배열을 주므로 첫 값을 본다.
 * @param {unknown} raw
 * @param {number} [nowMs]
 * @returns {string}
 */
export function resolveMonthParam(raw, nowMs = Date.now()) {
  const value = Array.isArray(raw) ? raw[0] : raw;
  return isValidMonth(value) ? value : previousMonthKst(nowMs);
}
